<template>
  <div>
    <HeadSection title="Promo code categories" permissionOfSection="fortune_wheel">
      <template #actions>
        <a-col :span="14" class="d-flex justify-content-end" style="gap: 10px;">
          <a-button
            type="primary"
            size="large"
            :disabled="loading"
            style="font-size: 16px; font-weight: 300; border-radius: 2px;"
            @click="toggleShowPromocodeModal(true)"
          >
            Create
          </a-button>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="userLoading" permissionOfSection="fortune_wheel">
      <div class="d-flex flex-wrap mb-3" style="gap: 10px;">
        <SearchField
          :value="filters.name"
          :loading="loading"
          :disabled="loading"
          @search="changeSearchValue"
        />
        <div
          v-if="isFiltered"
          style="width: fit-content; flex: 1 1 auto;"
          class="d-flex justify-content-end align-items-end"
        >
          <a-button
            size="large"
            @click="resetAllFilters"
          >
            Reset filters
          </a-button>
        </div>
      </div>
      <a-table
        class="fortune-wheel-promocodes-table"
        :dataSource="data"
        :pagination="meta"
        :row-key="(record) => record.id"
        :customRow="customRow"
        :columns="columns"
        :loading="loading"
        @change="changePagination"
      >
        <template slot="id" slot-scope="id, record">
          <div class="link">
            <router-link class="text-primary" :to="`/fortune-wheel/promo-codes/${record.id}`">{{ id }}</router-link>
          </div>
        </template>
      </a-table>
    </MainContainer>
    <PromocodeModal
      v-if="showPromocodeModal"
      :show="showPromocodeModal"
      title="Promo Code category creating"
      @close="toggleShowPromocodeModal"
    />
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import SearchField from '@/components/custom/form/SearchField.vue'
import PromocodeModal from '@/views/apps/fortune-wheel/promocodes/components/PromocodeModal.vue'
import { setQueryParams, setFilters } from '@/composables/useSetQueryParams.js'
import { getTableMeta } from '@/composables/useGetTableMeta.js'
import { getFilledFilters } from '@/composables/useGetFilledFilters.js'

const wheelApiUrl = process.env.VUE_APP_WHEEL_API_URL

export default {
  props: {
    editPermission: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HeadSection,
    MainContainer,
    SearchField,
    PromocodeModal,
  },
  data: () => ({
    data: [],
    meta: {
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '40'],
    },
    loading: false,
    filters: {
      page: undefined,
      per_page: undefined,
      search: '',
    },
    columns: [
      {
        title: 'ID',
        dataIndex: 'id',
        width: '5%',
        scopedSlots: { customRender: 'id' },
      },
      {
        title: 'Category name',
        dataIndex: 'name',
        width: '20%',
        ellipsis: true,
        scopedSlots: { customRender: 'name' },
      },
      {
        title: 'Total amount',
        dataIndex: 'total_amount',
        width: '15%',
        scopedSlots: { customRender: 'total_amount' },
      },
      {
        title: 'Unused',
        dataIndex: 'total_unused',
        width: '15%',
        scopedSlots: { customRender: 'total_unused' },
      },
    ],
    showPromocodeModal: false,
  }),
  computed: {
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    isHasListItems() {
      return !!this.data?.length
    },
    isFiltered() {
      return Object.values(this.filters).some(v => v !== undefined && v !== null && v !== '')
    },
    hasListData() {
      return this.isHasListItems || this.isFiltered
    },
  },
  methods: {
    customRow(category) {
      return {
        on: {
          click: () => {
            if (!category) {
              return
            }
            this.$router.push(`/fortune-wheel/promo-codes/${category.id}`)
          },
        },
      }
    },
    toggleShowPromocodeModal(status = false) {
      this.showPromocodeModal = status
    },
    changeSearchValue(value) {
      this.filters.search = value
      this.fetchPromocodeCategoriesList()
    },
    async changePagination(pagination) {
      this.filters.page = pagination.current
      this.filters.per_page = pagination.pageSize

      this.fetchPromocodeCategoriesList()
    },
    getFilters() {
      setQueryParams(this.filters, this)
      if (!this.isFiltered) {
        return {}
      }

      return getFilledFilters({ filters: this.filters })
    },
    async fetchPromocodeCategoriesList() {
      this.loading = true

      const filters = this.getFilters()

      const url = `${wheelApiUrl}/admin/promocodes/categories`

      try {
        const response = await apiClient.get(url, { params: filters })

        if (response?.data?.status) {
          this.data = response?.data?.data || []
          this.meta = getTableMeta(response?.data?.meta)
          return
        }
        this.$notification.error({
          message: 'Error when trying to get Wheel of Fortune promo codes',
        })
      } catch (e) {
        console.log('Error when trying to get Wheel of Fortune promo codes', e)
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loading = false
      }
    },
    resetAllFilters() {
      Object.keys(this.filters).forEach(key => {
        this.filters[key] = undefined
      })
      this.fetchPromocodeCategoriesList()
    },
  },
  async mounted() {
    if (Object.keys(this.$route.query)?.length) {
      setFilters(this.$route.query, this)
    }
    this.fetchPromocodeCategoriesList()
  },
}
</script>

<style lang="scss">
.fortune-wheel-promocodes-table {
  .link {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  .ant-table-row {
    cursor: pointer;
  }
}
[data-kit-theme="default"] .ant-table-empty .ant-table-body {
  overflow: visible !important;
}
.ant-table-column-has-actions {
  overflow: visible !important;
}
</style>
